import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, Star, Shirt, Sparkles, ShoppingBag } from "lucide-react";
import Countdown from "@/components/Countdown";
import PaymentMethods from "@/components/PaymentMethods";
import { optimizedImage } from "@/lib/image";

export default function StorePage({ data }) {
  const { store, gadgets, merch } = data;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="store-page" className="bg-black">
      {/* Hero */}
      <section className="relative min-h-[92svh] w-full overflow-hidden flex flex-col justify-center px-6 md:px-12 lg:px-16 pt-32 pb-16">
        {/* ambient */}
        <div className="absolute inset-0 pointer-events-none">
          {store.backgroundImage && (
            <>
              <img
                src={optimizedImage(store.backgroundImage, { width: 1920 })}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black" />
            </>
          )}
          <div className="absolute top-1/3 left-1/4 w-[700px] h-[700px] rounded-full bg-accent/[0.06] blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #FACC15 0, #FACC15 10px, transparent 10px, transparent 32px)",
            }}
          />
        </div>

        <div className="relative max-w-[1600px] mx-auto w-full">
          <Link
            to="/"
            data-testid="back-home-btn"
            className="group inline-flex items-center gap-2 text-white/60 hover:text-accent transition-colors mb-10 self-start"
          >
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="font-body text-[10px] uppercase tracking-[0.3em]">Torna al sito</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Ashvolt Store
              </span>
            </div>
            <h1
              data-testid="store-page-title"
              className="font-heading uppercase text-white text-6xl md:text-8xl lg:text-[10rem] leading-[0.85] mb-8"
            >
              Apertura<br />Store.
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9 }}
            className="border-t border-white/10 pt-10"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              {/* Preorders game */}
              <div>
                <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-3">
                  Preordini edizioni gioco
                </div>
                <div className="font-heading uppercase text-white text-3xl md:text-5xl leading-[0.9] mb-6">
                  20 maggio 2027
                </div>
                <Countdown targetDate={data.countdowns.preorders.targetDate} testId="store-page-countdown-preorders" />
              </div>
              {/* Gadgets + Apparel */}
              <div>
                <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-3">
                  Gadget & Magliette
                </div>
                <div className="font-heading uppercase text-white text-3xl md:text-5xl leading-[0.9] mb-6">
                  15 agosto 2027
                </div>
                <Countdown targetDate={store.openingTargetDate} testId="store-page-countdown-gadgets" />
              </div>
            </div>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-2xl mt-10">
              Le edizioni del gioco aprono ai preordini il <span className="text-white">20 maggio 2027</span>.
              Gadget e magliette arrivano il <span className="text-white">15 agosto 2027</span>.
              Iscriviti alla lista prioritaria dalla home per essere avvisato via email.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Editions */}
      <section className="relative px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-white/10 overflow-hidden">
        {store.backgroundImage && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src={optimizedImage(store.backgroundImage, { width: 1920 })}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        )}
        <div className="relative max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="mb-12 md:mb-16"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Edizioni del gioco · Preordini 20 maggio 2027
              </span>
            </div>
            <h2 className="font-heading uppercase text-white text-5xl md:text-7xl leading-[0.9]">
              The Protocol Lite.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {store.placeholderItems.map((item, i) => {
              const isCollector = !!item.featured;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className={`relative group border flex flex-col overflow-hidden transition-colors duration-500 ${
                    isCollector
                      ? "border-accent/40 bg-[#1A1A1F] hover:border-accent"
                      : "border-white/10 bg-[#09090B] hover:border-white/25"
                  }`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={optimizedImage(item.image, { width: 900, height: 675 })}
                      alt={item.label}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
                    {isCollector && (
                      <div className="absolute top-5 left-5 flex items-center gap-2 border border-accent/50 bg-black/60 backdrop-blur-sm px-3 py-1.5">
                        <Star size={11} className="text-accent" />
                        <span className="font-body text-[9px] uppercase tracking-[0.3em] text-accent">
                          Edizione Limitata
                        </span>
                      </div>
                    )}
                    <div className="absolute top-5 right-5 flex items-center gap-2 border border-white/15 bg-black/60 backdrop-blur-sm px-3 py-1.5">
                      <Lock size={11} className="text-accent" />
                      <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/80">
                        {item.status}
                      </span>
                    </div>
                  </div>
                  <div className={`relative p-6 md:p-8 border-t backdrop-blur-sm flex-1 flex flex-col ${isCollector ? "border-accent/20 bg-black/70" : "border-white/10 bg-black/30"}`}>
                    <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                      The Protocol Lite
                    </div>
                    <div className={`font-heading uppercase leading-none transition-colors duration-500 ${isCollector ? "text-white text-3xl md:text-4xl group-hover:text-accent" : "text-white text-2xl md:text-3xl group-hover:text-accent"}`}>
                      {item.label}
                    </div>
                    {item.includes && (
                      <ul className="mt-5 space-y-2">
                        {item.includes.map((inc) => (
                          <li key={inc} className="flex items-start gap-3 font-body text-sm text-white/75 leading-snug">
                            <span className="mt-1.5 w-1 h-1 bg-accent shrink-0" />
                            {inc}
                          </li>
                        ))}
                      </ul>
                    )}
                    {item.apparel && (
                      <div className="mt-5 border-t border-white/10 pt-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Shirt size={14} className="text-accent" />
                          <span className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">
                            {item.apparel.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {item.apparel.options.map((opt) => (
                            <span key={opt} className="font-body text-[11px] uppercase tracking-[0.2em] text-white/85 border border-white/20 bg-white/[0.03] px-3 py-1.5">
                              {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-auto pt-6">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                          <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                            {item.preorderNote}
                          </span>
                        </div>
                        {item.price && (
                          <span className="font-heading text-2xl md:text-3xl leading-none text-white/40 select-none cursor-not-allowed" aria-disabled="true">
                            {item.price}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => e.preventDefault()}
                        className="w-full inline-flex items-center justify-center gap-3 border border-accent/40 bg-accent/[0.06] backdrop-blur-sm px-5 py-3.5 cursor-default hover:bg-accent/[0.1] transition-colors"
                      >
                        <ShoppingBag size={13} className="text-accent" />
                        <span className="font-heading text-base tracking-[0.15em] uppercase text-accent">
                          {item.preorderCta}
                        </span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gadgets */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="mb-12 md:mb-16 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
          >
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-12 h-px bg-accent" />
                <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                  {gadgets.eyebrow}
                </span>
              </div>
              <h2 className="font-heading uppercase text-white text-5xl md:text-7xl leading-[0.9]">
                {gadgets.title}
              </h2>
            </div>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-md">
              {gadgets.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {gadgets.items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="group relative border border-white/10 bg-[#09090B] aspect-[3/4] flex flex-col justify-between p-5 md:p-6 hover:border-accent/40 transition-colors duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="font-heading uppercase text-white/[0.05] text-[7rem] md:text-[8rem] leading-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="relative flex items-start justify-between">
                  <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/40">
                    {item.tag}
                  </span>
                  <Sparkles size={13} className="text-accent/70 group-hover:text-accent transition-colors" />
                </div>
                <div className="relative">
                  <div className="font-heading uppercase text-white text-xl md:text-2xl leading-tight group-hover:text-accent transition-colors duration-500 mb-3">
                    {item.label}
                  </div>
                  <div className="flex items-center gap-2 border-t border-white/10 pt-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/60">
                      {item.status}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Merch / Apparel */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-32 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                {merch.eyebrow} · Apertura 15 agosto 2027
              </span>
            </div>
            <h2 className="font-heading uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-6">
              {merch.title}
            </h2>
            <p className="font-body text-base md:text-lg text-white/60 max-w-xl leading-relaxed mb-3">
              {merch.description}
            </p>
            {merch.dateNote && (
              <div className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/60">
                  {merch.dateNote}
                </span>
              </div>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-5 grid grid-cols-2 gap-3 md:gap-4"
          >
            {["Felpa oversize", "T-Shirt A★", "Cappellino ricamato", "Longsleeve"].map((label, i) => (
              <div
                key={label}
                className="border border-white/10 bg-[#09090B] aspect-square flex flex-col justify-between p-4 md:p-5 hover:border-accent/40 transition-colors group"
              >
                <Shirt size={14} className="text-accent/70 group-hover:text-accent transition-colors" />
                <div>
                  <div className="font-heading uppercase text-white text-lg md:text-xl leading-tight mb-2 group-hover:text-accent transition-colors">
                    {label}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/60">
                      In sviluppo
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Payment methods */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Pagamenti sicuri
              </span>
            </div>
            <h2 className="font-heading uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.9] mb-5">
              Metodi di pagamento accettati.
            </h2>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-md">
              Al checkout potrai completare il preordine con Apple Pay, Visa, Mastercard o PayPal — con crittografia end-to-end.
            </p>
          </div>
          <div className="lg:col-span-6">
            <PaymentMethods />
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto text-center">
          <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-4">
            Due aperture, un solo store
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mt-8">
            <div>
              <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                Preordini gioco
              </div>
              <div className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.9]">
                20 maggio 2027
              </div>
            </div>
            <div>
              <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                Gadget & Magliette
              </div>
              <div className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.9]">
                15 agosto 2027
              </div>
            </div>
          </div>
          <p className="font-body text-base md:text-lg text-white/50 max-w-2xl mx-auto mt-10">
            Torna in questa pagina il giorno dell'apertura o iscriviti dalla home per essere avvisato via email.
          </p>
        </div>
      </section>
    </div>
  );
}
